import React from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import API from "../api";
import PageShell from "../components/ui/PageShell";
import Card from "../components/ui/Card";
import { Crest, teamPageHref } from "../components/ui/MatchRow";
import LoadingSpinner from "../components/LoadingSpinner";
import { usePageTitle } from "../hooks/usePageTitle";
import { CURRENT_SEASON } from "../constants/season";
import { COMPETITION_OPTIONS } from "../constants/competitionPrefs";
import { teamLogo } from "../utils/fixtureLogo";

const RESULT_CLASS = {
  W: "bg-green-600 text-white border-green-600",
  D: "bg-amber-400 text-amber-950 border-amber-400",
  L: "bg-red-600 text-white border-red-600",
};

const RESULT_WORD = { W: "Won", D: "Drew", L: "Lost" };

function competitionLabel(id) {
  return COMPETITION_OPTIONS.find((option) => option.id === id)?.label || "League";
}

function formatWhen(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString([], {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatDay(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString([], { day: "numeric", month: "short" });
}

function goalDiffLabel(value) {
  if (value == null || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  if (n > 0) return `+${n}`;
  return String(n);
}

function BackButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasHistory = location.key !== "default";
  return (
    <button
      type="button"
      onClick={() => (hasHistory ? navigate(-1) : navigate("/fixtures"))}
      className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 -ml-2 text-meta font-medium text-muted hover:text-ink hover:bg-white"
    >
      <ArrowLeftIcon className="h-4 w-4" aria-hidden />
      Back
    </button>
  );
}

function Stat({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[22px] leading-7 font-bold tabular-nums text-ink">{value}</p>
      <p className="text-[12px] text-muted">{label}</p>
    </div>
  );
}

function ResultBox({ result, size = "md" }) {
  const box = size === "sm" ? "h-[18px] w-[18px] text-[11px]" : "h-7 w-7 text-[12px]";
  return (
    <span
      className={`flex shrink-0 items-center justify-center rounded border font-bold leading-none ${box} ${
        RESULT_CLASS[result] || RESULT_CLASS.D
      }`}
    >
      {result}
    </span>
  );
}

export default function TeamPage() {
  const [params] = useSearchParams();
  const competition = params.get("competition") || "premier_league";
  const name = (params.get("name") || "").trim();
  usePageTitle(name || "Team");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["teamPage", competition, name, CURRENT_SEASON],
    enabled: Boolean(name),
    queryFn: () =>
      API.get(`/teams/${encodeURIComponent(name)}/page`, {
        params: { competition, season: CURRENT_SEASON },
      }).then((res) => res.data),
  });

  const title = data?.team || name || "Team";
  const logo = teamLogo(title, competition, data?.crestUrl) || data?.crestUrl || null;
  const table = data?.table;
  const next = data?.next;
  const recent = Array.isArray(data?.recent) ? data.recent : [];
  const you = data?.you;
  const diff = goalDiffLabel(table?.goalDiff);
  const form = recent.map((match) => match.result).filter(Boolean);
  const nextOpen =
    next && !next.postponed && next.match_date && new Date(next.match_date).getTime() > Date.now();

  return (
    <PageShell header={<BackButton />}>
      {!name ? (
        <Card>
          <p className="text-center text-muted">Pick a team from a match.</p>
        </Card>
      ) : isLoading ? (
        <LoadingSpinner message="Loading team..." />
      ) : isError || !data ? (
        <Card>
          <p className="text-center text-muted">Couldn't load this team.</p>
        </Card>
      ) : (
        <div className="space-y-4">
          <section className="rounded-xl border border-line bg-white shadow-sm p-4 md:p-6">
            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-4 min-w-0">
                {logo ? (
                  <img src={logo} alt="" className="h-14 w-14 md:h-16 md:w-16 object-contain shrink-0" />
                ) : (
                  <Crest name={title} logo={null} />
                )}
                <div className="min-w-0">
                  <h1 className="text-[22px] md:text-[28px] leading-tight font-bold text-ink truncate">{title}</h1>
                  <p className="mt-0.5 text-meta text-muted">{competitionLabel(competition)}</p>
                  {form.length > 0 && (
                    <div className="mt-2 flex items-center gap-1" aria-label={`Form ${form.join("")}`}>
                      {form.map((result, index) => (
                        <ResultBox key={`${result}-${index}`} result={result} size="sm" />
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {table ? (
                <div className="grid grid-cols-4 gap-4 md:gap-8 border-t border-gray-100 pt-4 md:border-0 md:pt-0">
                  <Stat label="Place" value={`#${table.position}`} />
                  <Stat label="Points" value={table.points} />
                  <Stat label="Played" value={table.played ?? "–"} />
                  <Stat label="Goal diff" value={diff ?? "–"} />
                </div>
              ) : (
                <p className="text-meta text-muted">No league table for this competition.</p>
              )}
            </div>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] gap-4">
            <Card title="Next match">
              {next ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <Crest name={next.opponent} logo={teamLogo(next.opponent, competition)} />
                    <div className="min-w-0">
                      <p className="text-[12px] text-muted">{next.is_home ? "Home vs" : "Away at"}</p>
                      <Link
                        to={teamPageHref(competition, next.opponent)}
                        className="block truncate text-[17px] font-semibold text-ink hover:underline"
                      >
                        {next.opponent}
                      </Link>
                      <p className="text-meta text-muted">
                        {next.postponed ? "Postponed" : formatWhen(next.match_date)}
                      </p>
                    </div>
                  </div>
                  {nextOpen && (
                    <Link
                      to={`/predictions?competition=${encodeURIComponent(competition)}&fixture=${next.id}`}
                      className="shrink-0 rounded-full bg-brand px-4 py-2 text-meta font-semibold text-white hover:opacity-90"
                    >
                      Predict
                    </Link>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted">No upcoming match.</p>
              )}
            </Card>

            <Card title="Your picks">
              {you?.picks > 0 ? (
                <div className="flex items-baseline gap-2">
                  <p className="text-[28px] leading-8 font-bold tabular-nums text-ink">{Number(you.avg).toFixed(1)}</p>
                  <p className="text-meta text-muted">
                    avg · {you.points} pts from {you.picks} {you.picks === 1 ? "pick" : "picks"}
                  </p>
                </div>
              ) : (
                <p className="text-sm text-muted">No scored picks with this team yet.</p>
              )}
            </Card>
          </div>

          <Card title="Last five">
            {recent.length === 0 ? (
              <p className="text-sm text-muted">No results yet.</p>
            ) : (
              <ul className="divide-y divide-gray-100 -my-2">
                {recent.map((match) => (
                  <li key={match.id} className="flex items-center gap-3 py-3">
                    <ResultBox result={match.result} />
                    <Crest name={match.opponent} logo={teamLogo(match.opponent, competition)} size="sm" />
                    <div className="min-w-0 flex-1">
                      <Link
                        to={teamPageHref(competition, match.opponent)}
                        className="block truncate text-[15px] font-medium text-ink hover:underline"
                      >
                        {match.opponent}
                      </Link>
                      <p className="text-[12px] text-muted">
                        {match.is_home ? "Home" : "Away"}
                        {match.match_date ? ` · ${formatDay(match.match_date)}` : ""}
                      </p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-[15px] font-semibold tabular-nums text-ink">{match.scoreline}</p>
                      <p className="text-[12px] text-muted">{RESULT_WORD[match.result] || ""}</p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}
    </PageShell>
  );
}
